import { create } from 'zustand'
import { apiAdapter } from '@/lib/api-adapter'

interface Comment {
  id: string
  content: string
  author: {
    id: string
    name: string
    avatar?: string
    level: number
  }
  createdAt: Date
  likes: number
  liked: boolean
  replies?: Comment[]
  replyTo?: {
    id: string
    name: string
  }
}

interface Message {
  id: string
  senderId: string
  receiverId: string
  content: string
  type: 'text' | 'image' | 'file'
  createdAt: Date
  read: boolean
}

interface Conversation {
  id: string
  participantId: string
  participantName: string
  participantAvatar?: string
  lastMessage?: string
  lastMessageTime?: Date
  unreadCount: number
}

interface InteractionState {
  likes: Record<string, { count: number; liked: boolean }>
  comments: Record<string, Comment[]>
  shares: Record<string, number>
  messages: Message[]
  conversations: Conversation[]
  selectedConversationId: string | null
  isLoading: boolean

  // 初始化数据
  initializeData: () => void

  // 点赞相关
  toggleLike: (postId: string) => Promise<void>
  getLikes: (postId: string) => { count: number; liked: boolean }

  // 评论相关
  addComment: (postId: string, content: string, replyTo?: string) => Promise<void>
  likeComment: (commentId: string) => Promise<void>
  fetchComments: (postId: string) => Promise<void>
  getComments: (postId: string) => Comment[]
  reportComment: (commentId: string) => void

  // 分享相关（纯浏览器行为，本地计数）
  incrementShare: (postId: string, platform: string) => void
  getShares: (postId: string) => number

  // 私信相关（后端暂未实现，保留签名占位）
  sendMessage: (receiverId: string, content: string, type?: 'text' | 'image') => Promise<void>
  markAsRead: (messageId: string) => void
  getConversation: (userId: string) => Message[]
  getUnreadCount: () => number
  selectConversation: (conversationId: string) => void
}

// 后端评论 → 客户端结构映射
const fromServer = (c: any): Comment => ({
  id: c._id,
  content: c.content || '',
  author: {
    id: c.author?._id || '',
    name: c.author?.nickname || c.author?.username || '匿名用户',
    avatar: c.author?.avatar || '',
    level: 1
  },
  createdAt: new Date(c.createdAt),
  likes: c.likeCount || 0,
  liked: false,
  replies: (c.replies || []).map(fromServer),
  replyTo: c.replyTo ? { id: c.replyTo._id, name: c.replyTo.nickname || c.replyTo.username } : undefined
})

export const useInteractionStore = create<InteractionState>()((set, get) => ({
  likes: {},
  comments: {},
  shares: {},
  messages: [],
  conversations: [],
  selectedConversationId: null,
  isLoading: false,

  initializeData: () => {
    // 数据全部来自后端，无需本地初始化假数据
  },

  toggleLike: async (postId: string) => {
    try {
      const response = await apiAdapter.posts.likePost(postId)
      if (response.success && response.data) {
        const { isLiked, likeCount } = response.data
        set(state => ({
          likes: {
            ...state.likes,
            [postId]: { count: likeCount ?? (state.likes[postId]?.count ?? 0), liked: isLiked }
          }
        }))
      }
    } catch (error) {
      console.error('点赞失败:', error)
      throw error
    }
  },

  getLikes: (postId: string) => {
    const { likes } = get()
    return likes[postId] || { count: 0, liked: false }
  },

  fetchComments: async (postId: string) => {
    set({ isLoading: true })
    try {
      const response = await apiAdapter.comments.getComments(postId, { page: 1, limit: 50 })
      if (response.success && response.data) {
        const serverComments = response.data.comments || []
        set(state => ({
          comments: { ...state.comments, [postId]: serverComments.map(fromServer) },
          isLoading: false
        }))
      } else {
        set({ isLoading: false })
        throw new Error(response.message || '获取评论失败')
      }
    } catch (error) {
      set({ isLoading: false })
      throw error
    }
  },

  addComment: async (postId: string, content: string, replyTo?: string) => {
    try {
      const response = await apiAdapter.comments.createComment({
        postId,
        content,
        parentCommentId: replyTo || undefined,
        replyToUserId: undefined,
        isAnonymous: false
      })

      if (!response.success) {
        throw new Error(response.message || '评论失败')
      }

      // 评论成功后刷新该帖子的评论列表
      await get().fetchComments(postId)
    } catch (error) {
      console.error('评论失败:', error)
      throw error
    }
  },

  likeComment: async (commentId: string) => {
    try {
      const response = await apiAdapter.comments.likeComment(commentId)
      if (response.success && response.data) {
        const { isLiked, likeCount } = response.data
        // 更新本地所有评论（含回复）中的该评论
        set(state => {
          const updatedComments = { ...state.comments }
          Object.keys(updatedComments).forEach(postId => {
            updatedComments[postId] = updatedComments[postId].map(comment => {
              const updateOne = (c: Comment): Comment => {
                if (c.id === commentId) {
                  return { ...c, liked: isLiked, likes: likeCount ?? c.likes }
                }
                if (c.replies) {
                  return { ...c, replies: c.replies.map(updateOne) }
                }
                return c
              }
              return updateOne(comment)
            })
          })
          return { comments: updatedComments }
        })
      }
    } catch (error) {
      console.error('评论点赞失败:', error)
      throw error
    }
  },

  getComments: (postId: string) => {
    const { comments } = get()
    return comments[postId] || []
  },

  reportComment: () => {
    // 后端暂无举报接口，占位（UI 入口保留，提交时提示）
    console.warn('举报功能暂未开放')
  },

  incrementShare: (postId: string, platform: string) => {
    set(state => ({
      shares: {
        ...state.shares,
        [postId]: (state.shares[postId] || 0) + 1
      }
    }))
  },

  getShares: (postId: string) => {
    const { shares } = get()
    return shares[postId] || 0
  },

  // ---- 私信相关：后端暂无消息接口，以下为空实现（UI 已隐藏入口） ----

  sendMessage: async () => {
    throw new Error('消息功能暂未开放')
  },

  markAsRead: () => {},

  getConversation: () => [],

  getUnreadCount: () => 0,

  selectConversation: (conversationId) => {
    set({ selectedConversationId: conversationId })
  }
}))
